// Eigene Effekt-Plugins über die Bridge: beschreiben, offline rechnen, live mitrechnen, Original-Fenster.
//
// Ablauf je Spur bzw. Bus mit eigenen Plugins (siehe fxstate.ts):
//   1. Eingang rechnet der Browser (Clips/Instrument und Inserts vor dem ersten eigenen Plugin; beim Bus die Summe der Sends)
//   2. Eingang geht einmal an die Bridge (Binärblöcke, Schlüssel = Stand des Eingangs)
//   3. Offline: die Bridge rechnet die Kette, das Ergebnis wird Track.bridgeRender (wie Freeze) und für alle im Camp hochgeladen
//   4. Live (Spuren, wenn die Bridge hier verbunden ist): während der Wiedergabe kommen Stücke von je 8192 Samples,
//      Reglerbewegungen sind nach etwa einer halben Sekunde hörbar
//   5. Original-Fenster: nur auf Klick im Mixer; Bild und Maus über die Bridge, Änderungen kommen als pluginChanged zurück
import { create } from 'zustand';
import type { ChainSlotMsg, InsertSlot, PluginParamInfo, Project, Track } from '@srs/shared';
import { BIN, makeBin } from '@srs/shared';
import { bridge } from '../bridge/client.ts';
import { useStudio } from '../state/store.ts';
import { engine } from './engine.ts';
import { isolate, songEnd } from './render.ts';
import { clipEnd } from '../edit/ops.ts';
import { encodeWav } from './recorder.ts';
import { api } from '../api/client.ts';
import { uid } from '../state/demo.ts';
import { bussesOf } from './plugins.ts';
import { bridgeRange, drySig, fx, fxSig, hasBridge, hash2, isBridgeSlot, rendered } from './fxstate.ts';

const SR = 48000;
const CHUNK = 8192;          // Live-Stück (170 ms)
const LOOKAHEAD = 0.6;       // so weit voraus wird live gerechnet (s)
const UPLOAD_FRAMES = 32768; // Eingang in Blöcken von 256 KB

// ---------------------------------------------------------------------------
// Zustand für die Oberfläche
export type FxState = 'aktuell' | 'live' | 'rechnet' | 'wartet' | 'ohne-bridge' | 'fehlt' | 'fehler';
export interface FxStatus { state: FxState; percent?: number; msg?: string }
export interface PluginDesc { pluginId: string; name: string; vendor: string; latency: number; tail: number; params: PluginParamInfo[] }

export const useFx = create<{
  status: Record<string, FxStatus>;
  descs: Record<string, PluginDesc>;
  frames: Record<string, { url: string; w: number; h: number }>;
  /** zählt hoch, wenn eine Kette auf der Bridge bereit ist (Fenster können dann öffnen) */
  chainsRev: number;
}>()(() => ({ status: {}, descs: {}, frames: {}, chainsRev: 0 }));

const setStatus = (id: string, s: FxStatus) => useFx.setState((x) => (JSON.stringify(x.status[id]) === JSON.stringify(s) ? x : { status: { ...x.status, [id]: s } }));

/** Alle Kanäle mit Insert-Ketten (Spuren und Busse) */
const channels = (p: Project): Track[] => [...p.tracks.filter((t) => t.kind !== 'bus'), ...bussesOf(p)];
const isBusId = (p: Project, id: string) => bussesOf(p).some((b) => b.id === id);
export const chainIdOf = (id: string) => (/^[A-Za-z0-9_-]{1,32}$/.test(id) ? id : 'c' + hash2(id));
const pluginIdOf = (s: InsertSlot) => s.plugin.slice(7);

/** Regler eines Insert-Slots für die Bridge (nur "p<Index>"-Schlüssel) */
export const chainMsg = (s: InsertSlot): ChainSlotMsg => ({
  pluginId: pluginIdOf(s),
  ...(s.state ? { state: s.state } : {}),
  params: Object.fromEntries(Object.entries(s.params).filter(([k]) => /^p\d+$/.test(k))),
  ...(s.bypass ? { bypass: true } : {}),
});

/** Anzeigename eines eigenen Plugins: Hersteller und Name, ohne Doppelung ("FabFilter Pro-C 2") */
export const pluginLabel = (vendor: string, name: string) => (!vendor || name.toLowerCase().startsWith(vendor.toLowerCase()) ? name : `${vendor} ${name}`);

const localPlugins = () => new Set(bridge.status.plugins.map((p) => p.id));
const canRender = (t: Track) => bridge.effects && t.inserts.filter(isBridgeSlot).every((s) => localPlugins().has(pluginIdOf(s)));

// ---------------------------------------------------------------------------
// Beschreibung der Regler (zwischengespeichert)
const descWait = new Map<string, Promise<PluginDesc | null>>();

export function describe(pluginId: string): Promise<PluginDesc | null> {
  const have = useFx.getState().descs[pluginId];
  if (have) return Promise.resolve(have);
  try {
    const cached = localStorage.getItem(`srs.pdesc.${pluginId}`);
    if (cached) { const d = JSON.parse(cached) as PluginDesc; useFx.setState((x) => ({ descs: { ...x.descs, [pluginId]: d } })); return Promise.resolve(d); }
  } catch { /* Speicher nicht verfügbar */ }
  if (!bridge.effects) return Promise.resolve(null);
  let w = descWait.get(pluginId);
  if (!w) {
    w = (async () => {
      try {
        const id = bridge.send({ type: 'describePlugin', pluginId, samples: 33 });
        const m = await bridge.waitFor('pluginDescription', (x) => x.pluginId === pluginId, 60000, id);
        const d: PluginDesc = { pluginId, name: m.name, vendor: m.vendor, latency: m.latency, tail: m.tail, params: m.params };
        useFx.setState((x) => ({ descs: { ...x.descs, [pluginId]: d } }));
        try { localStorage.setItem(`srs.pdesc.${pluginId}`, JSON.stringify(d)); } catch { /* voll */ }
        // Kurzfassung ins Projekt, damit alle im Camp die Reglerliste sehen
        const p = useStudio.getState().project;
        if (!p.pluginInfo?.[pluginId]) useStudio.getState().commit('Plugin-Regler', (q) => {
          q.pluginInfo ??= {};
          q.pluginInfo[pluginId] = { name: d.name, vendor: d.vendor, params: d.params.filter((x) => x.auto).slice(0, 400).map((x) => ({ i: x.i, name: x.name, label: x.label, steps: x.steps })) };
        }, { silent: true });
        return d;
      } catch { return null; }
      finally { descWait.delete(pluginId); }
    })();
    descWait.set(pluginId, w);
  }
  return w;
}

/** Anzeige eines Reglerwerts aus den Stützstellen der Beschreibung (nächste Stelle) */
export function textAt(d: PluginDesc | undefined, i: number, v: number): string {
  const par = d?.params.find((x) => x.i === i);
  if (!par || !par.texts.length) return `${Math.round(v * 100)} %`;
  return par.texts[Math.round(v * (par.texts.length - 1))];
}

// ---------------------------------------------------------------------------
// Eingang an die Bridge
const uploaded = new Set<string>();
const dryCache = new Map<string, { sig: string; buf: AudioBuffer }>();   // Kanal → letzter Eingang

async function uploadInput(key: string, buf: AudioBuffer) {
  if (uploaded.has(key)) return;
  const id = bridge.send({ type: 'inputBegin', inputKey: key, channels: 2, frames: buf.length, sampleRate: buf.sampleRate });
  const stored = bridge.waitFor('inputStored', (m) => m.inputKey === key, 180000, id);
  const L = buf.getChannelData(0), R = buf.numberOfChannels > 1 ? buf.getChannelData(1) : L;
  for (let off = 0; off < buf.length; off += UPLOAD_FRAMES) {
    const n = Math.min(UPLOAD_FRAMES, buf.length - off);
    const { buf: msg, payload } = makeBin({ kind: BIN.audioIn, channels: 2, slot: 0, a: off, b: n, key }, n * 8);
    const f = new Float32Array(payload.buffer, payload.byteOffset, n * 2);
    f.set(L.subarray(off, off + n), 0);
    f.set(R.subarray(off, off + n), n);
    await bridge.sendBinary(msg);
  }
  await stored;
  uploaded.add(key);
}

/** Eingang eines Kanals rechnen (Browser): Spur ab 0 s bis zum Ende ihrer Clips, Bus bis Songende */
async function dryOf(p: Project, t: Track): Promise<{ sig: string; buf: AudioBuffer }> {
  const sig = drySig(p, t);
  const have = dryCache.get(t.id);
  if (have?.sig === sig) return have;
  let buf: AudioBuffer;
  if (isBusId(p, t.id)) {
    buf = await engine.bounce(p, Math.min(songEnd(p) + 1, 900), 0, { busInput: t.id });
  } else {
    const r = bridgeRange(t)!;
    const q = isolate(p, [t.id], 'commit', {});
    const x = q.tracks[0];
    x.inserts = t.inserts.slice(0, r.first);
    x.bridgeRender = undefined;
    const end = Math.max(1, ...p.clips.filter((c) => c.trackId === t.id).map(clipEnd));
    buf = await engine.bounce(q, Math.min(end + 1, 900));
  }
  const out = { sig, buf };
  dryCache.set(t.id, out);
  return out;
}

// ---------------------------------------------------------------------------
// Offline rechnen
async function renderOnBridge(chain: ChainSlotMsg[], inputKey: string, tail: number, onProgress: (pct: number) => void): Promise<AudioBuffer> {
  const jobId = 'j' + hash2(uid('j')).slice(0, 20);
  const parts: { at: number; n: number; data: Float32Array<ArrayBuffer> }[] = [];
  const offBin = bridge.onBinary((b) => { if (b.kind === BIN.renderOut && b.key === jobId) parts.push({ at: b.a, n: b.b, data: new Float32Array(b.data) }); });
  const offProg = bridge.on((m) => { if (m.type === 'renderProgress' && m.jobId === jobId) onProgress(m.percent); });
  try {
    const id = bridge.send({ type: 'renderAudio', jobId, inputKey, chain, tail, bpm: useStudio.getState().project.bpm });
    const done = await bridge.waitFor('renderAudioDone', (m) => m.jobId === jobId, 15 * 60000, id);
    // Binärblöcke kommen vor der Fertigmeldung; kurz warten, falls der letzte noch unterwegs ist
    for (let k = 0; k < 20 && parts.reduce((a, x) => a + x.n, 0) < done.frames; k++) await new Promise((r) => setTimeout(r, 25));
    const out = new AudioBuffer({ length: Math.max(1, done.frames), numberOfChannels: 2, sampleRate: done.sampleRate });
    for (const x of parts) {
      out.copyToChannel(x.data.subarray(0, x.n), 0, x.at);
      out.copyToChannel(x.data.subarray(x.n, 2 * x.n), 1, x.at);
    }
    return out;
  } finally { offBin(); offProg(); }
}

/** Puffer durch eingebaute Plugins schicken (für eingebaute Inserts zwischen eigenen Plugins) */
async function processInternal(p: Project, buf: AudioBuffer, slots: InsertSlot[]): Promise<AudioBuffer> {
  const sid = uid('tmp');
  engine.buffers.set(sid, buf);
  try {
    const t: Track = { id: 'tmp-fx', name: 'tmp', color: '#000', kind: 'audio', armed: false, mute: false, solo: false, volumeDb: 0, pan: 0, inserts: slots, sends: [] };
    const q: Project = { ...p, tracks: [t], clips: [{ id: 'tmp-c', trackId: t.id, kind: 'audio', name: 'tmp', start: 0, offset: 0, duration: buf.duration, sourceId: sid, fadeIn: 0, fadeOut: 0, by: { userId: 'fx', at: new Date(0).toISOString() } }], busses: [], masterInserts: [], masterDb: 0, mixBus: undefined, masterAutomation: undefined };
    return await engine.bounce(q, buf.duration);
  } finally { engine.buffers.delete(sid); }
}

/** Einen Kanal rechnen: Eingang, dann die Inserts vom ersten bis zum letzten eigenen Plugin */
async function renderChannel(channelId: string): Promise<'ok' | 'stale' | 'error'> {
  const p0 = useStudio.getState().project;
  const t = channels(p0).find((x) => x.id === channelId);
  const r = t && bridgeRange(t);
  if (!t || !r) return 'stale';
  const sig = fxSig(p0, t);
  setStatus(t.id, { state: 'rechnet', percent: 0 });
  try {
    const dry = await dryOf(p0, t);
    let cur = dry.buf, key = 'i' + hash2(dry.sig + '|' + SR);
    const range = t.inserts.slice(r.first, r.last + 1);
    // Abschnitte: eigene Plugins am Stück an die Bridge, eingebaute dazwischen im Browser
    const groups: { bridge: boolean; slots: InsertSlot[] }[] = [];
    for (const s of range) {
      const b = isBridgeSlot(s);
      if (groups.length && groups[groups.length - 1].bridge === b) groups[groups.length - 1].slots.push(s);
      else groups.push({ bridge: b, slots: [s] });
    }
    let latency = 0;
    for (const [gi, g] of groups.entries()) {
      if (g.bridge) {
        const chain = g.slots.map(chainMsg);
        for (let attempt = 0; ; attempt++) {
          try {
            await uploadInput(key, cur);
            cur = await renderOnBridge(chain, key, isBusId(p0, t.id) ? 6 : 3, (pct) => setStatus(t.id, { state: 'rechnet', percent: Math.round(((gi + pct / 100) / groups.length) * 100) }));
            break;
          } catch (e) {
            if (attempt === 0 && /Eingang fehlt/.test((e as Error).message)) { uploaded.delete(key); continue; }
            throw e;
          }
        }
      } else {
        cur = await processInternal(p0, cur, g.slots);
      }
      key = 'i' + hash2(key + JSON.stringify(g.slots.map((s) => [s.plugin, s.params, s.state, s.bypass])));
    }
    // Inzwischen geändert? Dann verwerfen, die Warteschlange rechnet neu
    const p1 = useStudio.getState().project, t1 = channels(p1).find((x) => x.id === channelId);
    if (!t1 || fxSig(p1, t1) !== sig) return 'stale';
    const sid = uid('src');
    engine.buffers.set(sid, cur);
    useStudio.getState().commit('Eigene Plugins gerechnet', (q) => {
      const x = channels(q).find((y) => y.id === channelId) ?? q.busses?.find((y) => y.id === channelId);
      if (x) x.bridgeRender = { sourceId: sid, duration: cur.duration, sig, upTo: r.last, latency };
    }, { silent: true });
    if (useStudio.getState().transport.playing) engine.reschedule();
    // Für alle im Camp hochladen
    const st = useStudio.getState();
    if (st.token) void api.uploadWav(encodeWav(cur), `${sid}.wav`, st.project.id).then((m) => { if (m) { engine.buffers.set(m.id, cur); useStudio.getState().replaceSourceId(sid, m.id); } }).catch(() => undefined);
    return 'ok';
  } catch (e) {
    setStatus(t.id, { state: 'fehler', msg: (e as Error).message });
    return 'error';
  }
}

// ---------------------------------------------------------------------------
// Warteschlange: Spuren vor Bussen, immer nur ein Auftrag
let running = false, again = false, lastChange = 0;
const BUS_QUIET = 3000;   // Busse erst rechnen, wenn 3 s nichts geändert wurde (Fader, Sends)
let timer: ReturnType<typeof setTimeout> | null = null;
const failed = new Map<string, string>();   // Kanal → Stand, bei dem es scheiterte (nicht endlos wiederholen)

export function scheduleRenders(delay = 1200) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { timer = null; void runQueue(); }, delay);
}

async function runQueue() {
  if (running) { again = true; return; }
  running = true;
  try {
    do {
      again = false;
      for (;;) {
        const p = useStudio.getState().project;
        const quiet = Date.now() - lastChange >= BUS_QUIET;
        const todo = channels(p).filter((t) => hasBridge(t) && !rendered(p, t) && canRender(t) && failed.get(t.id) !== fxSig(p, t));
        const next = todo.find((t) => !isBusId(p, t.id) || quiet);
        if (!next) { if (todo.length) scheduleRenders(BUS_QUIET); break; }
        const sig = fxSig(p, next);
        const res = await renderChannel(next.id);
        if (res === 'error') failed.set(next.id, sig);
      }
    } while (again);
  } finally { running = false; updateStatus(); }
}

function updateStatus() {
  const p = useStudio.getState().project;
  for (const t of channels(p)) {
    if (!hasBridge(t)) continue;
    const cur = useFx.getState().status[t.id];
    if (cur?.state === 'rechnet' && running) continue;
    if (cur?.state === 'fehler' && failed.get(t.id) === fxSig(p, t)) continue;
    const st: FxStatus = fx.live.has(t.id) ? { state: 'live' }
      : rendered(p, t) ? { state: 'aktuell' }
        : !bridge.effects ? { state: 'ohne-bridge' }
          : !canRender(t) ? { state: 'fehlt', msg: 'Plugin ist auf diesem Studio-Rechner nicht installiert' }
            : { state: 'wartet' };
    setStatus(t.id, st);
  }
}

// ---------------------------------------------------------------------------
// Live-Ketten (Spuren: Wiedergabe; Busse: nur Original-Fenster und Regler)
interface LiveChain { channelId: string; chainId: string; inputKey: string; sent: string; ready: boolean; bridgeSlots: number[]; contiguous: boolean }
const live = new Map<string, LiveChain>();          // channelId → Kette
const byChain = new Map<string, string>();          // chainId → channelId
let liveTimer: ReturnType<typeof setTimeout> | null = null;
let liveBusy = false, liveAgain = false;
/** Kanal, dessen Plugin-Fenster gerade aufgeht: wird zuerst geladen */
let priority = '';
export function prioritizeChannel(id: string) { priority = id; scheduleLive(0); }
// Platzhalter-Eingang (0,1 s Stille): damit lädt die Kette sofort, Fenster und Regler gehen gleich;
// der echte Eingang der Spur (rechnen und hochladen dauert) wird danach in dieselbe Kette getauscht.
const SILENT_KEY = 'z' + SR;
let silentBuf: AudioBuffer | null = null;
const bumpChains = () => useFx.setState((x) => ({ chainsRev: x.chainsRev + 1 }));

export function scheduleLive(delay = 80) {
  if (liveTimer) clearTimeout(liveTimer);
  liveTimer = setTimeout(() => { liveTimer = null; void syncLive(); }, delay);
}

async function syncLive() {
  if (liveBusy) { liveAgain = true; return; }
  liveBusy = true;
  try {
    do {
      liveAgain = false;
      const p = useStudio.getState().project;
      const want = bridge.effects ? channels(p).filter((t) => hasBridge(t) && canRender(t)) : [];
      // Entfernte Ketten schließen
      for (const [cid, c] of live) if (!want.some((t) => t.id === cid)) {
        if (bridge.effects) bridge.send({ type: 'liveClose', chainId: c.chainId });
        live.delete(cid); byChain.delete(c.chainId); fx.live.delete(cid);
      }
      want.sort((a, b) => (a.id === priority ? -1 : b.id === priority ? 1 : 0));
      // 1. Ketten ohne Eingang sofort laden (schnell: nur die Plugins)
      for (const t of want) {
        if (live.has(t.id)) continue;
        silentBuf ??= new AudioBuffer({ length: SR / 10, numberOfChannels: 2, sampleRate: SR });
        await uploadInput(SILENT_KEY, silentBuf);
        const bridgeSlots = t.inserts.map((s, i) => (isBridgeSlot(s) ? i : -1)).filter((i) => i >= 0);
        const r = bridgeRange(t)!;
        const chain = bridgeSlots.map((i) => chainMsg(t.inserts[i]));
        const chainId = chainIdOf(t.id);
        const entry: LiveChain = { channelId: t.id, chainId, inputKey: SILENT_KEY, sent: JSON.stringify({ inputKey: SILENT_KEY, chain }), ready: false, bridgeSlots, contiguous: bridgeSlots.length === r.last - r.first + 1 };
        live.set(t.id, entry); byChain.set(chainId, t.id);
        const id = bridge.send({ type: 'liveOpen', chainId, inputKey: SILENT_KEY, chain, bpm: p.bpm });
        try {
          await bridge.waitFor('liveReady', (m) => m.chainId === chainId, 60000, id);
          entry.ready = true;
          bumpChains();
        } catch (e) {
          live.delete(t.id);
          setStatus(t.id, { state: 'fehler', msg: (e as Error).message });
        }
      }
      // 2. Echten Eingang je Spur rechnen und in die Kette tauschen
      for (const t of want) {
        const r = bridgeRange(t)!;
        const bridgeSlots = t.inserts.map((s, i) => (isBridgeSlot(s) ? i : -1)).filter((i) => i >= 0);
        const contiguous = bridgeSlots.length === r.last - r.first + 1;
        // Busse: Kette nur für Original-Fenster und Regler; Eingang nicht bei jeder Fader-Bewegung neu rechnen
        const old = live.get(t.id);
        let inputKey: string;
        if (isBusId(p, t.id) && old && old.inputKey !== SILENT_KEY) inputKey = old.inputKey;
        else {
          const dry = await dryOf(p, t);
          inputKey = 'i' + hash2(dry.sig + '|' + SR);
          await uploadInput(inputKey, dry.buf);
        }
        const chain = bridgeSlots.map((i) => chainMsg(t.inserts[i]));
        const msg = JSON.stringify({ inputKey, chain });
        const chainId = chainIdOf(t.id);
        const c = live.get(t.id);
        if (c && c.sent === msg) continue;
        const entry: LiveChain = { channelId: t.id, chainId, inputKey, sent: msg, ready: c?.ready ?? false, bridgeSlots, contiguous };
        live.set(t.id, entry); byChain.set(chainId, t.id);
        const id = bridge.send({ type: 'liveOpen', chainId, inputKey, chain, bpm: p.bpm });
        try {
          await bridge.waitFor('liveReady', (m) => m.chainId === chainId, 60000, id);
          entry.ready = true;
          bumpChains();
          // Live-Wiedergabe nur für Spuren mit eigenen Plugins am Stück (eingebaute dazwischen: gerechnete Fassung)
          const wasLive = fx.live.has(t.id);
          if (contiguous && !isBusId(p, t.id)) fx.live.add(t.id); else fx.live.delete(t.id);
          if (wasLive !== fx.live.has(t.id)) { engine.sync(useStudio.getState().project); if (useStudio.getState().transport.playing) engine.reschedule(); }
        } catch (e) {
          live.delete(t.id); fx.live.delete(t.id);
          setStatus(t.id, { state: 'fehler', msg: (e as Error).message });
        }
      }
      // Eingang geändert, Kette aber noch mit altem Eingang: nicht live spielen, bis aktualisiert
      for (const [cid, c] of live) {
        const t = channels(useStudio.getState().project).find((x) => x.id === cid);
        if (t && !isBusId(useStudio.getState().project, cid) && c.inputKey !== 'i' + hash2(drySig(useStudio.getState().project, t) + '|' + SR) && fx.live.delete(cid)) engine.sync(useStudio.getState().project);
      }
    } while (liveAgain);
  } finally { liveBusy = false; updateStatus(); }
}

/** Slot-Index in der Bridge-Kette für einen Insert */
export function chainSlotOf(channelId: string, slotId: string): { chainId: string; slot: number } | null {
  const c = live.get(channelId);
  const t = channels(useStudio.getState().project).find((x) => x.id === channelId);
  if (!c || !c.ready || !t) return null;
  const idx = t.inserts.findIndex((s) => s.id === slotId);
  const slot = c.bridgeSlots.indexOf(idx);
  return slot < 0 ? null : { chainId: c.chainId, slot };
}

// ---------------------------------------------------------------------------
// Live-Wiedergabe
let playing = false, nextFrame = 0, nextCtx = 0;
/** Zähler für die Fehlersuche: angekommene, verspätete (später begonnene) und verworfene Stücke */
export const liveStats = { chunks: 0, late: 0, dropped: 0 };
const pending = new Map<string, { channelId: string; ctxAt: number }>();
let pumpTimer: number | null = null;

function pump() {
  const ctx = engine.ctx;
  if (!playing || !ctx) return;
  const p = useStudio.getState().project;
  const looping = p.loop.enabled && p.loop.end - p.loop.start > 0.05;
  const loopEnd = Math.round(p.loop.end * SR), loopStart = Math.round(p.loop.start * SR);
  const chains = [...live.values()].filter((c) => fx.live.has(c.channelId) && c.ready);
  if (!chains.length) return;
  while (nextCtx < ctx.currentTime + LOOKAHEAD) {
    let frames = CHUNK;
    if (looping && nextFrame < loopEnd) frames = Math.max(1, Math.min(CHUNK, loopEnd - nextFrame));
    for (const c of chains) {
      bridge.send({ type: 'liveProcess', chainId: c.chainId, from: nextFrame, frames });
      pending.set(`${c.chainId}:${nextFrame}`, { channelId: c.channelId, ctxAt: nextCtx });
    }
    nextFrame += frames;
    nextCtx += frames / SR;
    if (looping && nextFrame >= loopEnd && loopEnd > loopStart) nextFrame = loopStart;
  }
}

bridge.onBinary((b) => {
  if (b.kind === BIN.liveOut) {
    const key = `${b.key}:${b.a}`, want = pending.get(key);
    pending.delete(key);
    const ctx = engine.ctx;
    if (!want || !playing || !ctx || !engine.graph) return;
    const data = new Float32Array(b.data), n = b.b;
    const ab = new AudioBuffer({ length: n, numberOfChannels: 2, sampleRate: SR });
    ab.copyToChannel(data.subarray(0, n), 0);
    ab.copyToChannel(data.subarray(n, 2 * n), 1);
    const src = ctx.createBufferSource();
    src.buffer = ab;
    src.connect(engine.graph.strip(want.channelId).input);
    const late = ctx.currentTime - want.ctxAt;
    liveStats.chunks++;
    if (late > 0) liveStats.late++;
    if (late >= n / SR) { liveStats.dropped++; return; }
    src.start(Math.max(ctx.currentTime, want.ctxAt), Math.max(0, late));
    engine.addSource(src);
  } else if (b.kind === BIN.editorFrame) {
    const k = `${b.key}#${b.slot}`;
    const url = URL.createObjectURL(new Blob([b.data], { type: 'image/jpeg' }));
    const old = useFx.getState().frames[k];
    useFx.setState((x) => ({ frames: { ...x.frames, [k]: { url, w: b.a, h: b.b } } }));
    if (old) setTimeout(() => URL.revokeObjectURL(old.url), 1000);
  }
});

engine.live = {
  start(from: number) {
    pending.clear();
    playing = true;
    nextFrame = Math.round(from * SR);
    nextCtx = engine.ctxTimeOf(from);
    if (pumpTimer) clearInterval(pumpTimer);
    pumpTimer = window.setInterval(pump, 30);
    pump();
  },
  stop() {
    playing = false;
    pending.clear();
    if (pumpTimer) clearInterval(pumpTimer);
    pumpTimer = null;
  },
  startDelay() { return [...live.values()].some((c) => fx.live.has(c.channelId)) ? 0.25 : 0; },
};

// ---------------------------------------------------------------------------
// Original-Fenster und Regleränderungen aus dem Plugin
/** Offene Fenster zählen: dasselbe Fenster kann mehrfach angefordert werden (z. B. zwei Ansichten), geschlossen wird beim letzten */
const editorRefs = new Map<string, number>();
// Absturzschutz: Stürzt die Bridge ab, während ein Plugin-Fenster aufgeht, merkt sich das Studio das Plugin
// und öffnet sein Fenster nicht mehr von selbst (sonst öffnet es nach dem Neustart wieder und die Bridge
// stürzt erneut ab). Die Regler gehen weiter.
const CRASH_KEY = 'srs.editorCrash';
const pendingOpen = new Set<string>();
const crashes = (): Record<string, number> => { try { return JSON.parse(localStorage.getItem(CRASH_KEY) ?? '{}') as Record<string, number>; } catch { return {}; } };
export const editorCrashed = (pluginId: string) => !!crashes()[pluginId];
export function clearEditorCrash(pluginId: string) { const c = crashes(); delete c[pluginId]; try { localStorage.setItem(CRASH_KEY, JSON.stringify(c)); } catch { /* ohne Speicher */ } }
bridge.onStatus((st) => {
  if (st.connected || !pendingOpen.size) return;
  const c = crashes();
  for (const id of pendingOpen) c[id] = Date.now();
  pendingOpen.clear();
  try { localStorage.setItem(CRASH_KEY, JSON.stringify(c)); } catch { /* ohne Speicher */ }
});
export async function openEditor(channelId: string, slotId: string, pluginId = ''): Promise<{ chainId: string; slot: number } | null> {
  const cs = chainSlotOf(channelId, slotId);
  if (!cs) return null;
  const k = `${cs.chainId}#${cs.slot}`;
  editorRefs.set(k, (editorRefs.get(k) ?? 0) + 1);
  if (pluginId) pendingOpen.add(pluginId);
  try {
    const id = bridge.send({ type: 'openEditor', chainId: cs.chainId, slot: cs.slot });
    await bridge.waitFor('editorOpened', (m) => m.chainId === cs.chainId && m.slot === cs.slot, 20000, id);
    return cs;
  } catch (e) { closeEditor(cs); throw e; }
  finally { if (pluginId) setTimeout(() => pendingOpen.delete(pluginId), 4000); }   // Absturz kurz nach dem Öffnen zählt mit
}
export function closeEditor(cs: { chainId: string; slot: number }) {
  const k = `${cs.chainId}#${cs.slot}`, n = (editorRefs.get(k) ?? 1) - 1;
  if (n > 0) { editorRefs.set(k, n); return; }
  editorRefs.delete(k);
  if (bridge.effects) bridge.send({ type: 'closeEditor', chainId: cs.chainId, slot: cs.slot });
  const f = useFx.getState().frames[k];
  if (f) { URL.revokeObjectURL(f.url); useFx.setState((x) => { const n = { ...x.frames }; delete n[k]; return { frames: n }; }); }
}
export function editorInput(cs: { chainId: string; slot: number }, e: { kind: 'down' | 'up' | 'move' | 'wheel' | 'key'; x?: number; y?: number; button?: number; mods?: number; dx?: number; dy?: number; key?: string; keyDown?: boolean }) {
  bridge.send({ type: 'editorInput', chainId: cs.chainId, slot: cs.slot, x: 0, y: 0, ...e });
}

bridge.on((m) => {
  if (m.type !== 'pluginChanged') return;
  const channelId = byChain.get(m.chainId), c = channelId ? live.get(channelId) : undefined;
  if (!channelId || !c) return;
  const insertIndex = c.bridgeSlots[m.slot];
  const p = useStudio.getState().project;
  const t = channels(p).find((x) => x.id === channelId);
  const s = t?.inserts[insertIndex];
  if (!s || !isBridgeSlot(s)) return;
  const changed = Object.entries(m.params).some(([k, v]) => Math.abs((s.params[k] ?? -1) - v) > 1e-6) || (m.state && m.state !== s.state);
  if (!changed) return;
  useStudio.getState().commit(`${s.label ?? 'Plugin'}: Regler`, (q) => {
    const x = channels(q).find((y) => y.id === channelId) ?? q.busses?.find((y) => y.id === channelId);
    const slot = x?.inserts[insertIndex];
    if (!slot || slot.id !== s.id) return;
    slot.params = { ...slot.params, ...m.params };
    slot.texts = { ...(slot.texts ?? {}), ...m.texts };
    if (m.state) slot.state = m.state;
    slot.ai = false;
  }, { merge: true });
  // Die Live-Kette kennt den Stand schon; nur den gemerkten Stand angleichen, damit nichts zurückgeschickt wird
  const t2 = channels(useStudio.getState().project).find((x) => x.id === channelId);
  if (t2) c.sent = JSON.stringify({ inputKey: c.inputKey, chain: c.bridgeSlots.map((i) => chainMsg(t2.inserts[i])) });
});

/**
 * Vor dem Export: warten, bis alle eigenen Plugins gerechnet sind (höchstens 5 Minuten).
 * Gibt false zurück, wenn etwas ohne gerechnete Fassung bleibt (ohne Bridge: trocken).
 */
export async function waitForRenders(onStep: (t: string) => void): Promise<boolean> {
  const open = () => { const p = useStudio.getState().project; return channels(p).filter((t) => hasBridge(t) && !rendered(p, t)); };
  if (!open().length) return true;
  if (!bridge.effects) { onStep('Bridge nicht verbunden: eigene Plugins fehlen im Export'); return false; }
  scheduleRenders(0);
  const end = Date.now() + 5 * 60000;
  while (open().length && Date.now() < end) {
    onStep(`Eigene Plugins werden auf dem Studio-Rechner gerechnet (${open().map((t) => t.name).join(', ')}) …`);
    await new Promise((r) => setTimeout(r, 500));
  }
  return !open().length;
}

// ---------------------------------------------------------------------------
// Audio gerechneter Fassungen anderer im Camp nachladen
const loading = new Set<string>();
function loadRenders(p: Project) {
  for (const t of channels(p)) {
    const id = t.bridgeRender?.sourceId;
    if (!id || engine.buffers.has(id) || loading.has(id) || id.startsWith('src-')) continue;
    loading.add(id);
    void api.media(id).then((d) => engine.decode(id, d)).then(() => {
      engine.sync(useStudio.getState().project);
      if (useStudio.getState().transport.playing) engine.reschedule();
      updateStatus();
    }).catch(() => undefined).finally(() => loading.delete(id));
  }
}

// ---------------------------------------------------------------------------
// Anstoßen bei Änderungen
let lastSig = '';
useStudio.subscribe((s, prev) => {
  if (s.project === prev.project) return;
  const p = s.project;
  loadRenders(p);
  const any = channels(p).some(hasBridge);
  if (!any && !live.size) return;
  const sig = channels(p).map((t) => (hasBridge(t) ? fxSig(p, t) : '')).join('|');
  if (sig === lastSig) return;
  lastSig = sig;
  lastChange = Date.now();
  scheduleLive(80);
  scheduleRenders(s.transport.playing ? 4000 : 1200);
  updateStatus();
});

bridge.onStatus((st) => {
  if (!st.connected || !st.paired) {
    uploaded.clear(); live.clear(); byChain.clear(); editorRefs.clear();
    if (fx.live.size) { fx.live.clear(); engine.sync(useStudio.getState().project); }
  } else {
    scheduleLive(300);
    scheduleRenders(500);
  }
  updateStatus();
});
